import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import GossipCard from "@/components/base/GossipCard";
import { gossipCategories, gossipList } from "@/mocks/gossipList";

type SortKey = "rank" | "heat";

const ALL = "全部";

export default function Rankings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || ALL;
  const [category, setCategory] = useState(
    gossipCategories.some((c) => c.key === initialCategory) ? initialCategory : ALL,
  );
  const [keyword, setKeyword] = useState(searchParams.get("q") || "");
  const [sortKey, setSortKey] = useState<SortKey>("rank");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    let list = gossipList.filter((item) => {
      const matchCategory = category === ALL || item.category === category;
      if (!matchCategory) return false;
      if (!kw) return true;
      const haystack = [item.title, item.summary, item.conflict, item.category, ...item.people, ...item.tags]
        .join(" ")
        .toLowerCase();
      return haystack.includes(kw);
    });
    list = [...list].sort((a, b) => (sortKey === "rank" ? a.rank - b.rank : b.heat - a.heat));
    return list;
  }, [category, keyword, sortKey]);

  const handleCategory = (key: string) => {
    setCategory(key);
    const next = new URLSearchParams(searchParams);
    if (key === ALL) next.delete("category");
    else next.set("category", key);
    setSearchParams(next, { replace: true });
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "瓜网30条爆料大盘点汇总",
      url: `${origin}/rankings`,
      inLanguage: "zh-CN",
      description:
        "30 条娱乐爆料大盘点完整汇总，支持按主题分类筛选与关键词搜索，每条附核心人物、冲突点与事件状态。",
      isPartOf: { "@type": "WebSite", name: "瓜网30", url: `${origin}/` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "30条大盘点", item: `${origin}/rankings` },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title="30条爆料大盘点汇总｜瓜网30完整榜单"
        description="瓜网30条爆料大盘点完整汇总，涵盖恋情绯闻、剧组风波、合约解约、舞台争议、商业代言、形象争议与综艺名场面，支持按分类筛选与关键词搜索，每条标注核心人物与冲突点。"
        keywords="瓜网30条爆料,瓜网30个瓜,瓜网30大盘点,瓜网30个事件,娱乐事件汇总榜单"
        canonicalPath="/rankings"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1 w-full">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-8 md:py-10">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "30条大盘点" }]} />
            <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
              <div className="max-w-3xl">
                <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950">
                  30 条爆料大盘点 · 完整榜单
                </h1>
                <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                  这里是完整收录的 30 条事件。可以按主题分类筛选，也可以用关键词搜索人物、作品或事件关键词。
                  每条都附核心人物与冲突点，点进去能看到完整的时间线。
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-foreground-500">排序</span>
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-200">
                  {(
                    [
                      { key: "rank", label: "按榜单顺序" },
                      { key: "heat", label: "按热度" },
                    ] as { key: SortKey; label: string }[]
                  ).map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      aria-pressed={sortKey === s.key}
                      onClick={() => setSortKey(s.key)}
                      className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                        sortKey === s.key
                          ? "bg-background-50 text-foreground-950 font-medium"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8 md:py-10" aria-labelledby="list-title">
          <h2 id="list-title" className="sr-only">
            条目列表
          </h2>

          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-6">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="主题分类筛选">
              {gossipCategories.map((c) => {
                const isActive = category === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => handleCategory(c.key)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm whitespace-nowrap border transition-colors cursor-pointer ${
                      isActive
                        ? "bg-primary-500 text-background-50 border-primary-500"
                        : "bg-background-100 text-foreground-700 border-background-200 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={c.icon}></i>
                    </span>
                    {c.label}
                  </button>
                );
              })}
            </div>

            <div className="relative w-full lg:w-72 shrink-0">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400 pointer-events-none">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜人物、作品或事件关键词"
                aria-label="搜索条目"
                className="w-full pl-10 pr-10 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
              {keyword && (
                <button
                  type="button"
                  aria-label="清空搜索"
                  onClick={() => setKeyword("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-500 hover:text-foreground-900 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 text-sm text-foreground-600">
            <p>
              共找到 <strong className="text-primary-600">{filtered.length}</strong> 条
              {category !== ALL && <span> · 分类：{category}</span>}
              {keyword.trim() && <span> · 关键词：{keyword.trim()}</span>}
            </p>
            {(category !== ALL || keyword.trim()) && (
              <button
                type="button"
                onClick={() => {
                  handleCategory(ALL);
                  setKeyword("");
                }}
                className="flex items-center gap-1 text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            )}
          </div>

          {filtered.length > 0 ? (
            <div className="flex flex-col gap-4">
              {filtered.map((item) => (
                <GossipCard key={item.id} item={item} variant="row" />
              ))}
            </div>
          ) : (
            <div className="bg-background-100 border border-background-200 rounded-lg py-20 text-center">
              <span className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <p className="font-heading font-bold text-foreground-950 mb-2">没有找到匹配的条目</p>
              <p className="text-sm text-foreground-600 mb-5">试试换一个关键词，或者切回「全部大盘点」看看。</p>
              <button
                type="button"
                onClick={() => {
                  handleCategory(ALL);
                  setKeyword("");
                }}
                className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                查看全部 30 条
              </button>
            </div>
          )}

          <div className="mt-10 bg-secondary-100 border border-secondary-200 rounded-lg p-5 md:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-heading font-bold text-base text-foreground-950 mb-1">没看到你想找的那条？</h3>
              <p className="text-sm text-foreground-600">
                名单每周更新，欢迎把线索投给我们，写清时间和作品即可。
              </p>
            </div>
            <Link
              to="/contact"
              className="self-start sm:self-auto px-5 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              去投稿
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}